import { useCanEditEmailCampaigns } from '@components/Common/Utils/roles';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import ErrorMessage from '@components/Common/ErrorMessage';
import { PageHero } from '@components/Common/Panel';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { useToast } from '@components/Common/Toast/ToastProvider';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Box,
  Button,
  Chip,
  Tooltip,
} from '@mui/material';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import TemplateEditor from './TemplateEditor';
import { CATEGORY_LABELS, CATEGORY_ORDER, type EmailTemplateListItem } from './types';
import './index.scss';

/**
 * Template names arrive from seed/backend data with mixed casing (some ALL CAPS, some
 * lower, some mixed), which read as inconsistent in the list. Normalise every name to
 * Title Case at display so the column is uniform regardless of how it was stored.
 */
const toTitleCase = (name: string): string =>
  name.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

/**
 * Email templates — a list of artefacts, and an editor for one of them.
 *
 * Selecting a template hands the whole width to the editor shell rather than
 * squeezing source, preview and inspector into a third pane. The shell's back
 * link returns to this list, which is what "back to the parent list" means for
 * a surface that lives inside a tab.
 */
export default function EmailTemplates() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  // An HR Lead reads Email campaigns, and a template from its email record rather than the
  // editor, which autosaves as you type (owner, 2026-11-03). The rest is hidden from her and refused on the BFF (emailCampaignsAccess: GET only for her).
  const canEdit = useCanEditEmailCampaigns();

  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<EmailTemplateListItem[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const [backfilling, setBackfilling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);

  /** templateKey -> number of automation rules referencing it. */
  const [ruleUsage, setRuleUsage] = useState<Record<string, number> | null>(null);

  const loadTemplates = useCallback(() => {
    setListLoading(true);
    setListError(null);
    axios
      .get<EmailTemplateListItem[]>('/api/email-templates')
      .then((res) => setTemplates(res.data))
      .catch(() => setListError('Failed to load templates'))
      .finally(() => setListLoading(false));
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Usage is fetched for the list, so the author sees which templates feed live
  // automation BEFORE opening one — editing js-job-alert is a different
  // decision from editing an idle transactional template.
  useEffect(() => {
    axios
      .get<{ content: { templateKey: string }[] }>('/api/automation-rules?page=0&size=200')
      .then(({ data }) => {
        const counts: Record<string, number> = {};
        for (const rule of data?.content ?? []) {
          if (rule.templateKey) counts[rule.templateKey] = (counts[rule.templateKey] ?? 0) + 1;
        }
        setRuleUsage(counts);
      })
      .catch(() => setRuleUsage(null)); // the usage line simply does not render
  }, []);

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await axios.post<{ inserted: number; message: string }>(
        '/api/email-templates/backfill',
      );
      showToast(data.message, data.inserted > 0 ? 'success' : 'info');
      if (data.inserted > 0) loadTemplates();
    } catch {
      showToast('Failed to sync templates', 'error');
    } finally {
      setBackfilling(false);
    }
  };

  const handleRefreshSeeds = async () => {
    setRefreshConfirmOpen(false);
    setRefreshing(true);
    try {
      const { data } = await axios.post<{ updated: number; message: string }>(
        '/api/email-templates/refresh-seeds',
      );
      showToast(data.message, 'success');
      loadTemplates();
      setSelectedKey(null);
    } catch {
      showToast('Failed to refresh seed content', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const grouped = useMemo(
    () =>
      CATEGORY_ORDER.reduce<Record<string, EmailTemplateListItem[]>>((acc, cat) => {
        acc[cat] = templates.filter((t) => t.category === cat);
        return acc;
      }, {}),
    [templates],
  );

  const selected = templates.find((t) => t.templateKey === selectedKey) ?? null;

  /* ── Editor ───────────────────────────────────────────────────────────── */

  if (selectedKey) {
    return (
      <TemplateEditor
        key={selectedKey}
        templateKey={selectedKey}
        summary={selected}
        usageRuleCount={ruleUsage ? (ruleUsage[selectedKey] ?? 0) : null}
        onBack={() => setSelectedKey(null)}
        onSaved={(patch) =>
          setTemplates((prev) =>
            prev.map((t) => (t.templateKey === selectedKey ? { ...t, ...patch } : t)),
          )
        }
      />
    );
  }

  /* ── List ─────────────────────────────────────────────────────────────── */

  return (
    <Box className="email-templates-container">
      <Box className="et-page-body max-w-container">
        <PageHero
          kicker="Email Templates"
          title="Edit and manage all transactional, auth, and drip email templates."
          actions={canEdit ? (
            <>
              <Tooltip title="Sync missing templates from seed">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SyncIcon />}
                    onClick={handleBackfill}
                    disabled={backfilling}
                  >
                    {backfilling ? 'Syncing…' : 'Sync'}
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => setRefreshConfirmOpen(true)}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing…' : 'Refresh seeds'}
              </Button>
            </>
          ) : undefined}
        />

        {listLoading && <SkeletonLoader />}
        {listError && (
          <ErrorMessage message={listError} />
        )}

        {!listLoading && !listError && (
          <Box className="et-groups">
            {CATEGORY_ORDER.map((cat) => {
              const items = grouped[cat];
              if (!items || items.length === 0) return null;
              return (
                <Box key={cat} className="et-group">
                  <Box className="et-group-header">{CATEGORY_LABELS[cat] ?? cat}</Box>
                  <Box className="et-cards">
                    {items.map((t) => (
                      <button
                        key={t.templateKey}
                        type="button"
                        className={`et-card${!t.isActive ? ' is-paused' : ''}`}
                        onClick={() =>
                          canEdit
                            ? setSelectedKey(t.templateKey)
                            : navigate(`/email-campaigns/emails/${encodeURIComponent(t.templateKey)}`)
                        }
                      >
                        <Box className="et-card__head">
                          <span className="et-card__name">{toTitleCase(t.name)}</span>
                          <Chip
                            label={t.isActive ? 'Live' : 'Paused'}
                            size="small"
                            sx={{
                              backgroundColor: t.isActive ? 'var(--green-tint)' : 'var(--grey-tint)',
                              color: t.isActive ? 'var(--green)' : 'var(--grey)',
                            }}
                          />
                        </Box>
                        <span className="et-card__subject">{t.subject || 'No subject'}</span>
                        <Box className="et-card__foot">
                          <span className="et-card__key">{t.templateKey}</span>
                          {ruleUsage && (ruleUsage[t.templateKey] ?? 0) > 0 && (
                            <span className="et-card__usage">
                              {ruleUsage[t.templateKey]} rule
                              {ruleUsage[t.templateKey] === 1 ? '' : 's'}
                            </span>
                          )}
                        </Box>
                      </button>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={refreshConfirmOpen}
        title="Refresh seed content?"
        body={
          <>
            This overwrites <strong>all template content</strong> with the latest seed values. The
            current content is saved to version history automatically.
          </>
        }
        actionLabel="Refresh seeds"
        onConfirm={handleRefreshSeeds}
        onCancel={() => setRefreshConfirmOpen(false)}
      />
    </Box>
  );
}
